import { Canvas } from 'fabric';
import React, { FC, useEffect, useRef, useState } from 'react';
import { ClamshellCustomizationOptions, PersonalizeCaseProps, Styled } from '.';

import domtoimage from 'dom-to-image';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { useI18n } from 'contexts/i18n';

import { CanvasRefs, ClamshellPreview } from 'components/ClamshellPreview';
import {
  addLogoImageToCanvas,
  addQrCodeImageToCanvas,
  addWebUrlToCanvas,
  removeObject,
  useDeactivateCanvasObjects,
  useQRCode,
  validateWebsiteUrl,
} from './helpers';

import { LoadingSpinner } from 'styles/sharedStyles';
import { useCreateTemplate, useUpdateTemplate } from 'reactQuery/mutations';
import { Coordinates } from 'types';
import { Button } from 'components/core';

const PersonalizeCase: FC<PersonalizeCaseProps> = ({
  templateMode,
  setTemplateMode,
  selectedTemplate,
}) => {
  const canvasRefs = useRef<CanvasRefs>(null);
  const [logoCanvas, setLogoCanvas] = useState<Canvas | undefined>();
  const [qrCodeCanvas, setQRCodeCanvas] = useState<Canvas | undefined>();

  const [displayDrawingAreaOutline, setDisplayDrawingAreaOutline] =
    useState(true);
  const i18n = useI18n();

  const [selectedLogo, setSelectedLogo] = useState<File>();
  const [isLogoError, setIsLogoError] = useState(false);
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [qrCodeText, setQRCodeText] = useState('');
  const [isQrcodeSameAsWeburl, setQrcodeSameAsWeburl] = useState(true);
  const [designName, setDesignName] = useState('');
  const [logoCoordinates, setLogoCoordinates] = useState<Coordinates>();
  const [weburlCoordinates, setWeburlCoordinates] = useState<Coordinates>();
  const [qrcodeCoordinates, setqrCodeCoordinates] = useState<Coordinates>();
  const [isFetchingTemplate, setIsFetchingTemplate] = useState(false);

  const qrCodeImageWithHighResolution = useQRCode(qrCodeText, 200);
  useDeactivateCanvasObjects(logoCanvas, qrCodeCanvas);
  const createTemplateMutation = useCreateTemplate();
  const updateTemplateMutation = useUpdateTemplate();

  useEffect(() => {
    const options = {};

    let logoCanvasContainer: Canvas;
    let qrCodecanvasContainer: Canvas | undefined;

    if (canvasRefs.current?.logoCanvasContainerRef) {
      logoCanvasContainer = new Canvas(
        canvasRefs.current?.logoCanvasContainerRef.current ?? undefined,
        options
      );
      setLogoCanvas(logoCanvasContainer);
    }

    if (canvasRefs.current?.qrCodeCanvasContainerRef) {
      const qrCodecanvasContainer = new Canvas(
        canvasRefs.current?.qrCodeCanvasContainerRef.current ?? undefined,
        options
      );
      setQRCodeCanvas(qrCodecanvasContainer);
    }

    return () => {
      if (logoCanvasContainer && qrCodecanvasContainer !== undefined) {
        logoCanvasContainer.dispose();
        qrCodecanvasContainer.dispose();
      }

      setLogoCanvas(undefined);
      setQRCodeCanvas(undefined);
    };
  }, []);

  useEffect(() => {
    addQrCodeImageToCanvas(
      qrCodeCanvas,
      qrCodeImageWithHighResolution,
      templateMode,
      qrcodeCoordinates,
      setqrCodeCoordinates
    );
  }, [qrCodeImageWithHighResolution, templateMode]);

  useEffect(() => {
    addWebUrlToCanvas(
      qrCodeCanvas,
      websiteUrl,
      templateMode,
      weburlCoordinates,
      setWeburlCoordinates
    );
  }, [websiteUrl, templateMode]);

  useEffect(() => {
    if (selectedLogo) {
      addLogoImageToCanvas(
        logoCanvas,
        selectedLogo,
        templateMode,
        logoCoordinates,
        setLogoCoordinates
      );
    } else {
      removeObject(logoCanvas, 'logoImageId');
    }
  }, [selectedLogo, templateMode]);

  useEffect(() => {
    const renderLogo = async () => {
      if (selectedTemplate) {
        setIsFetchingTemplate(true);
        let blob = await fetch(selectedTemplate?.logoUrl).then(r => {
          return r.blob();
        });
        blob = new Blob([blob], { type: 'image/png' });
        const file = new File([blob], 'test.png');
        setSelectedLogo(file);
        setLogoCoordinates(selectedTemplate?.logoCoordinates);
        setIsFetchingTemplate(false);
      }
    };

    renderLogo();

    setWebsiteUrl(selectedTemplate?.webUrl || '');
    setQRCodeText(selectedTemplate?.qrcodeText || '');
    setQrcodeSameAsWeburl(
      selectedTemplate?.webUrl === selectedTemplate?.qrcodeText
    );
    setDesignName(selectedTemplate?.name || '');
    setWeburlCoordinates(selectedTemplate?.webUrlCoordinates);
    setqrCodeCoordinates(selectedTemplate?.qrcodeCoordinates);

    if (templateMode === 'select') {
      setDisplayDrawingAreaOutline(false);
    }
  }, [selectedTemplate, templateMode]);

  const onSave = async () => {
    setDisplayDrawingAreaOutline(false);
    const previewNode = canvasRefs.current?.previewCanvasContainerRef.current;
    let dataUrl;
    let blob;
    let previewFile;
    let logoFile;
    let qrcodeFile;

    if (logoCanvas && logoCanvas instanceof Canvas) {
      logoCanvas.discardActiveObject();
      logoCanvas.renderAll();
    }

    if (qrCodeCanvas && qrCodeCanvas instanceof Canvas) {
      qrCodeCanvas.discardActiveObject();
      qrCodeCanvas.renderAll();
    }

    if (previewNode) {
      dataUrl = await domtoimage.toBlob(previewNode);
      blob = new Blob([dataUrl]);
      previewFile = new File([blob], 'preview.png');
      setDisplayDrawingAreaOutline(true);
    }
    if (selectedLogo) {
      logoFile = new File([selectedLogo], 'logo.png');
    }

    if (qrCodeImageWithHighResolution) {
      let blob = await fetch(qrCodeImageWithHighResolution).then(r => r.blob());
      blob = new Blob([blob], { type: 'image/png' });
      qrcodeFile = new File([blob], 'qrcode.png');
    }

    if (selectedTemplate) {
      if (templateMode === 'edit') {
        return updateTemplateMutation.mutate({
          templateCode: selectedTemplate.code,
          templateName: designName,
          logoImage: logoFile,
          qrcodeImage: qrcodeFile,
          previewImage: previewFile,
          webUrl: websiteUrl,
          qrcodeText: qrCodeText,
          setTemplateMode: setTemplateMode,
          logo_coordinates: logoCoordinates,
          web_url_coordinates: weburlCoordinates,
          qrcode_coordinates: qrcodeCoordinates,
        });
      } else {
        //clone template
        return createTemplateMutation.mutateAsync({
          templateName: designName,
          logoImage: logoFile,
          qrcodeImage: qrcodeFile,
          previewImage: previewFile,
          webUrl: websiteUrl,
          qrcodeText: qrCodeText,

          setTemplateMode: setTemplateMode,
          logo_coordinates: logoCoordinates,
          web_url_coordinates: weburlCoordinates,
          qrcode_coordinates: qrcodeCoordinates,
        });
      }
    } else {
      return createTemplateMutation.mutateAsync({
        templateName: designName,
        logoImage: logoFile,
        qrcodeImage: qrcodeFile,
        previewImage: previewFile,
        webUrl: websiteUrl,
        qrcodeText: qrCodeText,
        setTemplateMode: setTemplateMode,
        logo_coordinates: logoCoordinates,
        web_url_coordinates: weburlCoordinates,
        qrcode_coordinates: qrcodeCoordinates,
      });
    }
  };

  const createPreview = async () => {
    setDisplayDrawingAreaOutline(false);
    const previewNode = canvasRefs.current?.previewCanvasContainerRef.current;
    if (logoCanvas) {
      logoCanvas.discardActiveObject();
      logoCanvas.renderAll();
    }

    if (qrCodeCanvas) {
      qrCodeCanvas.discardActiveObject();
      qrCodeCanvas.renderAll();
    }

    const zip = new JSZip();

    if (previewNode) {
      const dataUrl = await domtoimage.toBlob(previewNode);
      const blob = new Blob([dataUrl]);
      zip.file('preview.png', blob);
      setDisplayDrawingAreaOutline(true);
    }

    const qrcodenode = canvasRefs.current?.qrCodeCanvasContainerRef.current;

    if (qrcodenode) {
      const dataUrl = await domtoimage.toBlob(qrcodenode);
      const blob = new Blob([dataUrl]);
      zip.file('qrcode.png', blob);
      setDisplayDrawingAreaOutline(true);
    }

    if (qrCodeImageWithHighResolution) {
      let blob = await fetch(qrCodeImageWithHighResolution).then(r => r.blob());
      blob = new Blob([blob], { type: 'image/png' });
      zip.file('qrcode.png', blob);
    }

    if (selectedLogo) {
      zip.file('logo.png', selectedLogo);
      setDisplayDrawingAreaOutline(true);
    }

    zip.generateAsync({ type: 'blob' }).then(function (content) {
      // see FileSaver.js
      saveAs(content, 'design_preview.zip');
    });
  };

  function hasExtension(file: File) {
    const exts = ['.jpg', '.jpeg', '.png'];
    const fileName = file.name;
    return new RegExp('(' + exts.join('|').replace(/\./g, '\\.') + ')$').test(
      fileName
    );
  }

  const handleLogoChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const selectedLogo = event.target.files && event.target.files[0];

    if (selectedLogo) {
      const img = new Image();
      img.src = window.URL.createObjectURL(selectedLogo);
      img.onload = () => {
        console.log('onload called');
        if (
          (img.width >= 500 || img.height >= 200) &&
          hasExtension(selectedLogo)
        ) {
          setIsLogoError(false);
          setSelectedLogo(selectedLogo);
          return true;
        }
        setIsLogoError(true);
        setSelectedLogo(undefined);
        return true;
      };
    }
  };

  const shouldEnableSaveAction = () => {
    return (
      selectedLogo && validateWebsiteUrl(websiteUrl) && qrCodeText && designName
    );
  };

  return (
    <>
      {(createTemplateMutation.isLoading ||
        updateTemplateMutation.isLoading ||
        isFetchingTemplate) && (
        <Styled.LoaderContainer>
          <LoadingSpinner isPrimary={true} width="30px" height="30px" />
        </Styled.LoaderContainer>
      )}
      <Styled.Container
        isLoading={
          createTemplateMutation.isLoading ||
          updateTemplateMutation.isLoading ||
          isFetchingTemplate
        }
      >
        <Styled.ClamshellContainer>
          <Styled.PreviewWrapper>
            <ClamshellPreview
              createPreview={createPreview}
              ref={canvasRefs}
              displayDrawingAreaOutline={displayDrawingAreaOutline}
              displayLogoHelperText={!selectedLogo}
              displayQrCodeHelperText={websiteUrl === '' && qrCodeText === ''}
            />
            <br />
            <Styled.DownloadButtonWrapper>
              <Button
                size="md"
                variant="secondary"
                onClick={createPreview}
                disabled={!shouldEnableSaveAction()}
              >
                {i18n['Download Preview']}
              </Button>
            </Styled.DownloadButtonWrapper>
          </Styled.PreviewWrapper>
        </Styled.ClamshellContainer>

        <Styled.PersonalizationContainer>
          <ClamshellCustomizationOptions
            selectedLogo={selectedLogo}
            websiteUrl={websiteUrl}
            qrCodeText={qrCodeText}
            designName={designName}
            isQrcodeSameAsWeburl={isQrcodeSameAsWeburl}
            setSelectedLogo={setSelectedLogo}
            setWebsiteUrl={setWebsiteUrl}
            setQRCodeText={setQRCodeText}
            setDesignName={setDesignName}
            setQrcodeSameAsWeburl={setQrcodeSameAsWeburl}
            onSaveDesign={onSave}
            onLogoChange={handleLogoChange}
            setTemplateMode={setTemplateMode}
            templateMode={templateMode}
            selectedTemplate={selectedTemplate}
            shouldEnableSaveAction={shouldEnableSaveAction}
            isLogoError={isLogoError}
          />
        </Styled.PersonalizationContainer>
      </Styled.Container>
    </>
  );
};
export default PersonalizeCase;
